
import React, { useMemo, useEffect, useState, useCallback } from "react";
import { useMissionGuide } from "../../../../hooks/useMissionGuide";

function PhoneShell({ unlocked, children }) {
	return (
		<div className="w-full max-w-sm mx-auto">
			<div className="rounded-[28px] border border-white/10 bg-black/60 shadow-2xl overflow-hidden">
				<div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
					<div className="text-[11px] uppercase tracking-[0.2em] text-slate-300">
						Téléphone — {unlocked ? "Déverrouillé" : "Verrouillé"}
					</div>
					<div className="text-[11px] text-slate-400 font-mono">23:17</div>
				</div>
				<div className="p-4">{children}</div>
			</div>
		</div>
	);
}

function AppIcon({ emoji, label, onClick }) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="group rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition p-3 flex flex-col items-center gap-2"
		>
			<div className="w-12 h-12 rounded-2xl bg-black/40 border border-white/10 flex items-center justify-center text-xl group-hover:brightness-125">
				{emoji}
			</div>
			<div className="text-[11px] text-slate-200 tracking-wide">{label}</div>
		</button>
	);
}

function Cell({ id, active, onPick }) {
	return (
		<button
			type="button"
			onPointerDown={(e) => {
				e.preventDefault();
				onPick(id);
			}}
			className={[
				// taille proche des dots mais un peu plus "tap-friendly"
				"w-10 h-10 sm:w-11 sm:h-11",
				"rounded-full",
				"transition",
				"touch-manipulation select-none",

				// mêmes couleurs que l'énigme des formes
				active ? "bg-amber-200" : "bg-slate-600/40",

				// petite bordure discrète + hover, sans changer les couleurs principales
				"border border-white/10",
				!active ? "hover:bg-slate-600/55" : "hover:brightness-105",

				// focus accessible
				"focus:outline-none focus:ring-2 focus:ring-amber-300/40",
			].join(" ")}
			aria-label={`Point ${id}`}
		/>
	);
}

function ResultChip({ ch, state }) {
	const cls =
		state === "g"
			? "bg-emerald-500/15 border-emerald-400/40 text-emerald-100"
			: "bg-red-500/15 border-red-400/40 text-red-100";

	return (
		<div
			className={[
				"w-8 h-9 rounded-lg border flex items-center justify-center font-mono text-xs",
				cls,
			].join(" ")}
			title={state === "g" ? "Bonne lettre, bon endroit" : "Incorrect"}
		>
			{ch}
		</div>
	);
}


/** ---- Main component ---- */

export default function IncidentOrderPuzzle({ onSolved, unlocked = false, onUnlockFailed, code, playerId, pseudo }) {
	const [view, setView] = useState("home"); // "home" | "morse"
	const [pattern, setPattern] = useState([]);
	const [feedback, setFeedback] = useState("");

	const CORRECT_PATTERN = useMemo(() => [1, 2, 3, 5, 7, 8, 9], []);


	const { missionUnlock, missionResolve } = useMissionGuide({
		code,
		playerId,
		stage: 1,
	});

	const isPatternOk = useCallback(
		(p) => {
			const want = new Set(CORRECT_PATTERN);
			const got = new Set(p); // ignore l'ordre + ignore les doublons

			if (got.size !== want.size) return false;

			for (const v of want) {
				if (!got.has(v)) return false;
			}
			return true;
		},
		[CORRECT_PATTERN]
	);


	const pickCell = useCallback((id) => {
		setFeedback("");
		setPattern((prev) => {
			// si déjà sélectionnée -> on retire
			if (prev.includes(id)) return prev.filter((x) => x !== id);

			// sinon on ajoute (limite de sécu)
			if (prev.length >= 9) return prev;
			return [...prev, id];
		});
	}, []);


	const resetPattern = useCallback(() => {
		setPattern([]);
		setFeedback("");
	}, []);

	const submitPattern = useCallback(() => {
		if (isPatternOk(pattern)) {
			setFeedback("✅ Téléphone déverrouillé.");
			setView("home");
			onSolved?.();
		} else {
			setFeedback("❌ Mauvais schéma.");
			setPattern([]);
			onUnlockFailed?.();
		}
	}, [isPatternOk, pattern, onSolved]);

	// ---- MORSE VALIDATOR ----
	const TARGET = "MIRAGIO";
	const [guess, setGuess] = useState("");
	const [tries, setTries] = useState([]);
	//   const [mvFeedback, setMvFeedback] = useState("");
	const [homeFeedback, setHomeFeedback] = useState("");
	const [morseFeedback, setMorseFeedback] = useState("");

	// ✅ Cooldown anti bruteforce
	const COOLDOWN_MS = 10000;
	const [cooldownUntil, setCooldownUntil] = useState(0);
	const [cooldownNow, setCooldownNow] = useState(Date.now());

	const inCooldown = cooldownNow < cooldownUntil;
	const cooldownLeftSec = Math.max(0, Math.ceil((cooldownUntil - cooldownNow) / 1000));

	useEffect(() => {
		if (!cooldownUntil) return;

		// tick léger uniquement pendant le cooldown
		const t = setInterval(() => setCooldownNow(Date.now()), 200);
		return () => clearInterval(t);
	}, [cooldownUntil]);

	const normalize = (s) =>
		(s || "")
			.toUpperCase()
			.normalize("NFD")
			.replace(/\p{Diacritic}/gu, "")
			.replace(/[^A-Z]/g, "");

	const evalGuess = (word, target) => {
		const w = word.split("");
		const t = target.split("");
		// g = green, r = red
		return w.map((ch, i) => (ch === t[i] ? "g" : "r"));
	};

	const submitMorseWord = useCallback(() => {
		// ✅ blocage pendant cooldown
		const now = Date.now();
		if (now < cooldownUntil) {
			setMorseFeedback(`⏳ Patiente ${Math.ceil((cooldownUntil - now) / 1000)}s avant un nouvel essai.`);
			return;
		}

		const w = normalize(guess);
		if (w.length !== TARGET.length) {
			setMorseFeedback(`❌ Longueur de code invalide.`);
			return;
		}

		const result = evalGuess(w, TARGET);
		setTries((prev) => [...prev, { word: w, result }]);
		setGuess("");

		// ✅ déclenche cooldown après CHAQUE essai valide
		setCooldownUntil(now + COOLDOWN_MS);
		setCooldownNow(now);

		if (w === TARGET) {
			missionResolve({ key: "s1.p1.morseCode.opened" });
			missionResolve({ key: "s1.p4.dictionary.morseSeen" });
			missionResolve({ key: "s1.p3.morseValidator.found" });
			missionUnlock({
				key: "s1.p3.morseCode.found",
				text: `Le validateur de morse trouvé par ${pseudo} indique que "Miragio" est valide.`,
			});
		}

		setMorseFeedback(w === TARGET ? "Mot correct. Il s'agit sans doute d'un mot de passe..." : "✅ Test effectué.");
	}, [guess, cooldownUntil]);


	if (!unlocked) {
		return (
			<PhoneShell unlocked={unlocked}>
				<div className="space-y-3 flex flex-col align-items-center">
					<p className="text-xs text-gray-300 mb-1 mx-auto">Schéma de déverrouillage requis.</p>

					<div className="mx-auto inline-grid grid-cols-[repeat(3,auto)] gap-3">
						{[1, 2, 3, 4, 5, 6, 7, 8, 9].map((id) => (
							<Cell key={id} id={id} active={pattern.includes(id)} onPick={pickCell} />
						))}
					</div>

					<div className="flex gap-2">
						<button
							type="button"
							onClick={resetPattern}
							className="flex-1 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 py-2 text-xs font-semibold"
						>
							Effacer
						</button>
						<button
							type="button"
							onClick={submitPattern}
							className="flex-1 rounded-lg bg-indigo-600 hover:bg-indigo-500 py-2 text-xs font-semibold"
						>
							Déverrouiller
						</button>
					</div>

					{feedback && (
						<p className={["text-xs mx-auto", feedback.startsWith("✅") ? "text-emerald-300" : "text-amber-200"].join(" ")}>
							{feedback}
						</p>
					)}
				</div>
			</PhoneShell>
		);
	}

	return (
		<PhoneShell unlocked={unlocked}>
			{view === "home" && (
				<div className="space-y-3">
					<p className="text-xs text-gray-300">Bureau du téléphone (apps internes).</p>

					<div className="grid grid-cols-3 gap-3">
						<AppIcon emoji="🗒️" label="Notes" onClick={() => setHomeFeedback("🛈 Rien d’utile ici.")} />
						<AppIcon emoji="🔔" label="Notifications" onClick={() => setHomeFeedback(`🛈 Un nouveau log d'incident a été stocké sur votre ordinateur.`)} />
						<AppIcon emoji="📞" label="Appels" onClick={() => setHomeFeedback("🛈 Aucun réseau.")} />
						<AppIcon emoji="📟" label="Morse Validator" onClick={() => {
							setHomeFeedback("");
							missionUnlock({
								key: "s1.p3.morseValidator.found",
								text: `${pseudo} a trouvé une application appelée "Morse Validator".`,
							});
							setView("morse");
						}} />
						<AppIcon emoji="🖼️" label="Photos" onClick={() => setHomeFeedback("🛈 Dossier vide.")} />
						<AppIcon emoji="⚙️" label="Réglages" onClick={() => setHomeFeedback("🛈 Paramètres verrouillés.")} />
					</div>

					{homeFeedback && (
						<div className="rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-xs text-slate-200">
							{homeFeedback}
						</div>
					)}
				</div>
			)}

			{view === "morse" && (
				<div className="space-y-3">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm font-semibold text-slate-100">Morse Validator</p>

						</div>
						<button
							type="button"
							onClick={() => {
								setView("home");
								setMorseFeedback("");
							}}
							className="rounded-full bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-1.5 text-xs"
						>
							← Apps
						</button>
					</div>

					<div className="rounded-xl border border-white/10 bg-black/40 p-3 space-y-2">
						<div className="flex items-center gap-2">
							<input
								value={guess}
								onChange={(e) => {
									setGuess(e.target.value);
									setMorseFeedback("");
								}}
								autoComplete="off"
								autoCorrect="off"
								autoCapitalize="characters"
								spellCheck={false}
								className="flex-1 px-3 py-2 rounded-lg bg-black/60 border border-white/15 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
								placeholder={`Entrez le mot à tester`}
							/>
							<button
								type="button"
								onClick={submitMorseWord}
								disabled={inCooldown}
								className={[
									"rounded-lg px-3 py-2 text-xs font-semibold",
									inCooldown
										? "bg-indigo-600/40 cursor-not-allowed"
										: "bg-indigo-600 hover:bg-indigo-500",
								].join(" ")}
							>
								{inCooldown ? `Attends ${cooldownLeftSec}s` : "Tester"}
							</button>



						</div>
						{inCooldown && (
							<p className="text-[11px] text-slate-400">
								Prochain essai autorisé dans {cooldownLeftSec}s.
							</p>
						)}

						{morseFeedback && (
							<p className={["mx-auto", "text-xs", morseFeedback.startsWith("❌") ? "text-amber-200" : "text-emerald-300"].join(" ")}>
								{morseFeedback}
							</p>
						)}
					</div>

					{tries.length > 0 && (
						<div className="space-y-2">
							{tries
								.slice()
								.reverse()
								.map((t, idx) => (
									<div key={`${t.word}-${idx}`} className="rounded-xl border border-white/10 bg-white/5 p-2">
										<div className="flex gap-1 justify-center">
											{t.word.split("").map((ch, i) => (
												<ResultChip key={i} ch={ch} state={t.result[i]} />
											))}
										</div>
									</div>
								))}
						</div>
					)}
				</div>
			)}
		</PhoneShell>
	);
}
